// Core utility functions for the RUN.world API system
// These functions are used across multiple modules to handle real vs mock API delegation

// Helper function to create proxied methods that delegate to real API when available
export function createProxiedMethod(methodName, mockImpl) {
  return function (...args) {
    // For lifecycle handlers (methods that start with 'on'), store them locally
    if (methodName.startsWith('on') && typeof args[0] === 'function') {
      if (!this._bootstrap._localHandlers) {
        this._bootstrap._localHandlers = {}
      }

      // Store the handler locally
      this._bootstrap._localHandlers[methodName] = args[0]

      // If the real API is available, also register with it
      if (
        this._bootstrap.rundotGame &&
        typeof this._bootstrap.rundotGame[methodName] === 'function'
      ) {
        return this._bootstrap.rundotGame[methodName](...args)
      }

      return true
    }

    // For all other methods, if the real API is available and has the method, use it
    if (
      this._bootstrap.rundotGame &&
      typeof this._bootstrap.rundotGame[methodName] === 'function'
    ) {
      return this._bootstrap.rundotGame[methodName](...args)
    }

    // Otherwise, fall back to the mock implementation
    return mockImpl.apply(this, args)
  }
}

// Helper function to create proxied objects with methods and getters that delegate to real API
export function createProxiedObject(objectName, mockImpl) {
  const self = this // Store reference to 'this' for use in handlers

  // Helper to create recursive proxies
  function createNestedProxy(target, realApiPath) {
    const handler = {
      get: function (targetObj, prop, receiver) {
        // If the real API is available, use it
        if (self._bootstrap.rundotGame && realApiPath) {
          // Navigate to the correct path in the real API
          let realApiValue = self._bootstrap.rundotGame
          const pathParts = realApiPath.split('.')

          for (const part of pathParts) {
            if (realApiValue && realApiValue[part]) {
              realApiValue = realApiValue[part]
            } else {
              realApiValue = null
              break
            }
          }

          if (realApiValue && prop in realApiValue) {
            const realValue = Reflect.get(realApiValue, prop, receiver)

            // If the real value is an object (but not a function), create a nested proxy
            if (
              realValue &&
              typeof realValue === 'object' &&
              typeof realValue !== 'function'
            ) {
              return createNestedProxy(realValue, `${realApiPath}.${prop}`)
            }

            return realValue
          }
        }

        // Otherwise, get from the mock implementation
        const value = Reflect.get(targetObj, prop)

        // If it's a method, bind it to the RundotGameAPI instance
        if (typeof value === 'function') {
          return function (...args) {
            return value.apply(self, args)
          }
        }

        // If it's an object (but not null or array), create a nested proxy
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          const nestedPath = realApiPath ? `${realApiPath}.${prop}` : prop
          return createNestedProxy(value, nestedPath)
        }

        return value
      },
    }

    return new Proxy(target, handler)
  }

  return createNestedProxy(mockImpl, objectName)
}

// Helper function to check if we're on web platform (for feature detection)
export function isWebPlatform() {
  return typeof window !== 'undefined' && !window.ReactNativeWebView
}

// Standard mock delay (100ms for most operations, 1000ms for longer ones like ads/AI)
export const MOCK_DELAYS = {
  short: 100, // Quick operations (get/set preferences, etc.)
  medium: 500, // Medium operations (network-like calls)
  long: 1000, // Long operations (ads, AI, etc.)
}

// Centralized mock delay function
export function createMockDelay(ms = MOCK_DELAYS.short) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
